import { Radio, RadioGroup, Typography } from '@globalmed/atlas-ui';
import { memo, useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ESignatureSettingsRequiredBy,
  ESignatureSettingsType,
  TDocumentUserType,
} from '@/types';

type TProps = {
  withTitle?: boolean;
  type: ESignatureSettingsType;
  requiredBy: ESignatureSettingsRequiredBy;
  onSignatureTypeChange: (value: string) => void;
  onSignatureRequiredByChange: (value: string) => void;
  userType?: TDocumentUserType;
  allowedTypes?: ESignatureSettingsType[];
  withRequiredBy?: boolean;
};

const signatureTypeLabels: Record<ESignatureSettingsType, string> = {
  [ESignatureSettingsType.NO_SIGNATURE]: 'form-builder.signature.no-signature',
  [ESignatureSettingsType.TYPED]: 'form-builder.signature.typed-only',
  [ESignatureSettingsType.DRAW_AND_TYPED]:
    'form-builder.signature.drawn-and-typed',
};

const allSignatureTypes = [
  ESignatureSettingsType.NO_SIGNATURE,
  ESignatureSettingsType.TYPED,
  ESignatureSettingsType.DRAW_AND_TYPED,
];

export const FormBuilderSignature =
  /**
   * Signature settings component for configuring signature type and required participants
   * @param {TProps} props - Component props
   * @param {boolean} [props.withTitle] - Whether to show title and description
   * @param {ESignatureSettingsType} props.type - Current signature type
   * @param {ESignatureSettingsRequiredBy} props.requiredBy - Who is required to sign
   * @param {Function} props.onSignatureTypeChange - Callback when signature type changes
   * @param {Function} props.onSignatureRequiredByChange - Callback when required participants change
   * @param {TDocumentUserType} props.userType - The type of user for the document
   * @param {ESignatureSettingsType[]} [props.allowedTypes] - Signature types to offer, all of them by default
   * @param {boolean} [props.withRequiredBy] - Whether to show the participants group, true by default
   * @returns {JSX.Element} Form builder signature settings component
   */
  memo(
    ({
      withTitle,
      type,
      requiredBy,
      onSignatureRequiredByChange,
      onSignatureTypeChange,
      userType = 'patient',
      allowedTypes = allSignatureTypes,
      withRequiredBy = true,
    }: TProps) => {
      const { t } = useTranslation();
      const signatureTypeLabelId = useId();
      const requiredByLabelId = useId();
      const disableParticipants = type === ESignatureSettingsType.NO_SIGNATURE;

      return (
        <div>
          {withTitle && (
            <div style={{ marginBottom: '24px' }}>
              <Typography
                fontSize="20px"
                fontWeight="600"
                sx={{ lineHeight: '30px', marginBottom: '4px' }}
              >
                {t('common.signature-settings')}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight="400"
                color={'var(--colors-gray600)'}
                sx={{ lineHeight: '20px' }}
              >
                {t('form-builder.signature.settings-description')}
              </Typography>
            </div>
          )}
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Typography
                id={signatureTypeLabelId}
                fontSize="16px"
                fontWeight="600"
                sx={{ lineHeight: '24px', marginBottom: '12px' }}
              >
                {t('form-builder.signature.signature-type')}
              </Typography>
              <RadioGroup
                value={type}
                onChange={onSignatureTypeChange}
                direction="column"
                aria-labelledby={signatureTypeLabelId}
              >
                {allowedTypes.map((signatureType) => (
                  <Radio
                    key={signatureType}
                    label={t(signatureTypeLabels[signatureType])}
                    value={signatureType}
                    radioSize="md"
                  />
                ))}
              </RadioGroup>
            </div>
            {withRequiredBy && (
              <div>
                <Typography
                  id={requiredByLabelId}
                  fontSize="16px"
                  fontWeight="600"
                  sx={{ lineHeight: '24px', marginBottom: '12px' }}
                >
                  {t('form-builder.signature.add-signature-field-for')}
                </Typography>
                {userType === 'provider' ? (
                  <RadioGroup
                    value={requiredBy}
                    onChange={onSignatureRequiredByChange}
                    aria-labelledby={requiredByLabelId}
                  >
                    <Radio
                      label={t('form-builder.signature.provider-only')}
                      value={ESignatureSettingsRequiredBy.PROVIDER}
                      radioSize="md"
                      disabled={disableParticipants}
                    />
                  </RadioGroup>
                ) : (
                  <RadioGroup
                    value={requiredBy}
                    onChange={onSignatureRequiredByChange}
                    aria-labelledby={requiredByLabelId}
                  >
                    <Radio
                      label={t('form-builder.signature.patient-only')}
                      value={ESignatureSettingsRequiredBy.PATIENT}
                      radioSize="md"
                      disabled={disableParticipants}
                    />
                    <Radio
                      label={t('form-builder.signature.patient-and-provider')}
                      value={ESignatureSettingsRequiredBy.PATIENT_AND_PROVIDER}
                      radioSize="md"
                      disabled={disableParticipants}
                    />
                  </RadioGroup>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
  );

FormBuilderSignature.displayName = 'SignatureFormBuilder';
